import React, { ReactNode } from 'react';

import { ChatRuntimeProvider } from '@dev-streetbeat/chat/react';

import { useChatConfig } from 'shared/hooks/useChatConfig';


interface StreetBeatChatProviderProps {
    children: ReactNode;
    /**
     * URL-derived thread id (or null on /assistant). Switches the runtime into
     * controlled mode so v16's RuntimeThreadSessionSync mounts and populates
     * `activeThreadId` / `lastCreatedThreadId` in chatState — without this prop
     * the runtime is uncontrolled and the host never learns about new threads
     * created by `createThreadAndStart`.
     */
    threadId?: string | null;
}

export const StreetBeatChatProvider: React.FC<StreetBeatChatProviderProps> = ({
    children,
    threadId,
}) => {
    const { chatConfig, loading, error } = useChatConfig();

    if (error) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-red-500">
                    <p>Chat initialization failed: {error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-2 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="text-gray-500">
                    Loading user information...
                </div>
            </div>
        );
    }

    return (
        <ChatRuntimeProvider config={chatConfig} threadId={threadId ?? null}>
            {children}
        </ChatRuntimeProvider>
    );
};
